import { apiClient, unwrap } from '@/lib/apiClient';
import type { TeacherAllocation } from '@/types/allocation';
import type { ApiSuccess } from '@/types/api';
import type {
  AttendanceRegister,
  AttendanceSummary,
  RegisterEntryPayload,
} from '@/types/attendance';
import type { MyStaffAttendance, StaffPunch } from '@/types/staffAttendance';
import type { OwnStaffProfile } from '@/types/staff';
import type { OwnProfilePayload } from '../schemas/ownProfile.schema';
import type { TeacherOverview } from '@/types/teacherDashboard';

/**
 * Every network call the teacher console makes.
 *
 * The allotment read is deliberately its own endpoint rather than the institute
 * screen's `GET /institute/allocations?staffUuid=…`: this one resolves the teacher
 * from the caller's token, so there is no id in it for anybody to point at
 * somebody else's classes, and it needs no permission beyond being signed in.
 */
export const teacherApi = {
  /** The signed-in member of staff's own record, for My Profile. Read-only. */
  myProfile: () => unwrap(apiClient.get<ApiSuccess<OwnStaffProfile>>('/institute/staff/me')),

  /**
   * Saves the caller's own editable details. A new mobile number or bank account
   * needs `currentPassword`; the server answers with the updated record.
   */
  updateMyProfile: (payload: OwnProfilePayload) =>
    unwrap(apiClient.patch<ApiSuccess<OwnStaffProfile>>('/institute/staff/me', payload)),

  /** Where the caller's own photograph streams from. Fetched with the token. */
  myPhotoPath: () => '/institute/staff/me/photo',

  /** Replaces the caller's own photograph. The browser adds the multipart boundary. */
  uploadMyPhoto: (file: File) => {
    const form = new FormData();
    form.append('photo', file);
    return unwrap(
      apiClient.post<ApiSuccess<OwnStaffProfile>>('/institute/staff/me/photo', form, {
        headers: { 'Content-Type': undefined },
      }),
    );
  },

  removeMyPhoto: () =>
    unwrap(apiClient.delete<ApiSuccess<OwnStaffProfile>>('/institute/staff/me/photo')),

  /** The signed-in teacher's sections and subjects for the current year. */
  myAllocations: () =>
    unwrap(apiClient.get<ApiSuccess<TeacherAllocation[]>>('/institute/allocations/me')),

  /**
   * Every figure on the console's first screen, for one day.
   *
   * One call rather than one per card: the stat tiles, the schedule and the
   * performance cards are all questions about the same allotments, and fetched
   * separately they would drift apart the moment one was slower than the rest.
   *
   * `date` is omitted for today — which then means the *institute's* today,
   * decided in IST by the server, rather than whatever the device believes.
   */
  overview: (date?: string) =>
    unwrap(
      apiClient.get<ApiSuccess<TeacherOverview>>('/institute/dashboard/teacher', {
        params: date ? { date } : undefined,
      }),
    ),

  /**
   * One section's register for one day.
   *
   * `date` is omitted for today, which is the register a teacher opens 99
   * mornings in 100 — and leaving it off means the *institute's* today, decided in
   * IST by the server, rather than whatever the device's clock believes.
   */
  register: (classSectionUuid: string, date?: string) =>
    unwrap(
      apiClient.get<ApiSuccess<AttendanceRegister>>('/institute/attendance/register', {
        params: { classSectionUuid, ...(date && { date }) },
      }),
    ),

  /**
   * The same section totalled across a window of days — the reports screen.
   *
   * Both dates are optional and default on the server to the month so far, so
   * the screen opens on a real period without having to guess one from the
   * device's clock. The server also clamps them to the session and to today.
   */
  attendanceSummary: (classSectionUuid: string, from?: string, to?: string) =>
    unwrap(
      apiClient.get<ApiSuccess<AttendanceSummary>>('/institute/attendance/summary', {
        params: { classSectionUuid, ...(from && { from }), ...(to && { to }) },
      }),
    ),

  /**
   * Saves the day whole.
   *
   * Every child in one request rather than a mark at a time: a register
   * half-saved is a register that looks taken and is wrong, with nobody able to
   * say which half.
   */
  saveRegister: (classSectionUuid: string, date: string, entries: RegisterEntryPayload[]) =>
    unwrap(
      apiClient.put<ApiSuccess<AttendanceRegister>>('/institute/attendance/register', {
        classSectionUuid,
        date,
        entries,
      }),
    ),

  /**
   * The signed-in person's own attendance for one month.
   *
   * `month` is omitted for the month we are in. Like the register, the server
   * decides what "now" means in IST rather than trusting the device's clock.
   */
  myStaffAttendance: (month?: string) =>
    unwrap(
      apiClient.get<ApiSuccess<MyStaffAttendance>>('/institute/staff-attendance/me', {
        params: month ? { month } : undefined,
      }),
    ),

  /**
   * Records the caller's arrival.
   *
   * Sends no body at all: the date is today and the time is the server's clock.
   * A punch carrying its own timestamp would be a register anybody could write
   * themselves an alibi into.
   */
  punchIn: () =>
    unwrap(apiClient.post<ApiSuccess<StaffPunch>>('/institute/staff-attendance/punch-in')),

  /** The other end of the same day. Takes no body — the clock is the server's. */
  punchOut: () =>
    unwrap(apiClient.post<ApiSuccess<StaffPunch>>('/institute/staff-attendance/punch-out')),
};
